import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "@/lib/api";
import type { Me } from "@/lib/types";
import { LoginPage } from "@/pages/LoginPage";

type MeCtx = Me & { refresh: () => void };
const Ctx = createContext<MeCtx | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needLogin, setNeedLogin] = useState(false);
  const load = () => api.get<Me>("/me").then((m) => { setMe(m); setNeedLogin(false); setError(null); }).catch((e: ApiError) => { if (e.status === 401 && e.message === "login_required") setNeedLogin(true); else setError(e.message); });
  useEffect(() => { load(); }, []);
  if (needLogin) return <LoginPage onLoggedIn={load} />;
  if (error) {
    return (
      <div style={{ padding: 40, maxWidth: 520, margin: "10vh auto", textAlign: "center" }}>
        <img src="/assets/hixonops-wordmark.png" alt="HixonOps" style={{ height: 26, marginBottom: 24 }} />
        <h1 style={{ fontSize: 16, marginBottom: 8 }}>Can't sign you in</h1>
        <p className="muted-2">{error}</p>
      </div>
    );
  }
  if (!me) return <div className="loading" style={{ justifyContent: "center", minHeight: "40vh" }}><span className="spinner" /> Signing in</div>;
  return <Ctx.Provider value={{ ...me, refresh: load }}>{children}</Ctx.Provider>;
}

export function useMe(): MeCtx {
  const me = useContext(Ctx);
  if (!me) throw new Error("useMe outside MeProvider");
  return me;
}
